import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import {
  getAllCareers,
  deleteCareer,
  updateCareerStatus,
} from "../../services/Career/careerServices";
import { paginateData, calculateTotalPages } from "../../assets/js/script";
import TableHeader from "../Common/TableComponent/TableHeader";
import EntriesDropdown from "../Common/TableComponent/EntriesDropdown";
import TablesRow from "../Common/TableComponent/TablesRow";
import { Pagination } from "../Common/TableComponent/Pagination";
import { Loading } from "../Common/OtherElements/Loading";
import { TableDataStatusError } from "../Common/OtherElements/TableDataStatusError";
import { confirmDelete } from "../Common/OtherElements/confirmDeleteClone";
import { handleErrors } from "../../utils/errorHandler";
import { usePageLevelAccess } from "../../hooks/usePageLevelAccess";

export const ManageCareer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const PageLevelAccessurl = "career";
  const { pageAccessData } = usePageLevelAccess(PageLevelAccessurl);
  const [pageAccessDetails, setPageAccessDetails] = useState([]);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [careers, setCareers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (location.state?.successMessage) {
      toast.success(location.state.successMessage);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  useEffect(() => {
    if (pageAccessData) {
      if (!pageAccessData.viewAccess) {
        navigate("/404-error-page");
      } else {
        setPageAccessDetails(pageAccessData);
      }
    }
  }, [pageAccessData, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const data = await getAllCareers();
      setCareers(Array.isArray(data) ? data : []);
    } catch (error) {
      handleErrors(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filteredData = careers.filter((item) => {
    const query = searchQuery.toLowerCase();
    return (
      (item.role || "").toLowerCase().includes(query) ||
      (item.location || "").toLowerCase().includes(query) ||
      (item.url || "").toLowerCase().includes(query)
    );
  });

  const currentData = paginateData(filteredData, currentPage, entriesPerPage);
  const totalPages = calculateTotalPages(filteredData.length, entriesPerPage);

  const handleEntriesChange = (value) => {
    setEntriesPerPage(value);
    setCurrentPage(1);
  };

  const handleStatusToggle = async (item) => {
    const nextStatus = item.status === "Active" ? "Draft" : "Active";

    try {
      await updateCareerStatus(item.id, nextStatus);
      setCareers((prev) =>
        prev.map((career) =>
          career.id === item.id ? { ...career, status: nextStatus } : career
        )
      );
      toast.success(
        nextStatus === "Active" ? "Job set to active." : "Job moved to draft."
      );
    } catch (error) {
      handleErrors(error);
    }
  };

  const handleDelete = async (id) => {
    const confirmed = await confirmDelete("Job");
    if (!confirmed) return;

    try {
      await deleteCareer(id);
      setCareers((prev) => prev.filter((item) => item.id !== id));
      Swal.fire("Deleted!", "The job has been deleted successfully.", "success");
    } catch (error) {
      handleErrors(error);
    }
  };

  return (
    <>
      {pageAccessDetails.viewAccess ? (
        <div className="row">
          <div className="col-xxl-12">
            <div className="card mt-xxl-n5">
              <div className="card-header d-sm-flex align-items-center justify-content-between">
                <h5 className="mb-sm-2 mt-sm-2">Manage Jobs</h5>
                {pageAccessDetails.addAccess && (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => navigate("/career/add")}
                  >
                    Add Job
                  </button>
                )}
              </div>
              <div className="card-body manage-amenity-master-card-body">
                <div className="pagination-details-responsive justify-content-between mb-3">
                  <EntriesDropdown
                    entriesPerPage={entriesPerPage}
                    onEntriesChange={handleEntriesChange}
                  />
                  <div>
                    <input
                      type="text"
                      placeholder="Search..."
                      className="form-control mb-2"
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setCurrentPage(1);
                      }}
                    />
                  </div>
                </div>

                {loading ? (
                  <Loading />
                ) : (
                  <div className="table-responsive">
                    <table className="table align-middle table-bordered">
                      <TableHeader
                        columns={[
                          "#",
                          "Role",
                          "Location",
                          "URL",
                          "Status",
                          "Added On",
                          "Action",
                        ]}
                      />
                      <tbody className="manage-page-group-table-values">
                        {currentData.length === 0 ? (
                          <TableDataStatusError colspan="7" />
                        ) : (
                          currentData.map((item, index) => (
                            <TablesRow
                              key={item.id}
                              rowData={{
                                id:
                                  (currentPage - 1) * entriesPerPage +
                                  index +
                                  1,
                                role: item.role || "-",
                                location: item.location || "-",
                                url: item.url || "-",
                                status: (
                                  <div className="form-check form-switch form-switch-custom form-switch-primary mb-0">
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      role="switch"
                                      checked={item.status === "Active"}
                                      disabled={!pageAccessDetails.editAccess}
                                      aria-label={
                                        item.status === "Active"
                                          ? "Move job to draft"
                                          : "Set job to active"
                                      }
                                      onChange={() => handleStatusToggle(item)}
                                    />
                                  </div>
                                ),
                                addedOn: item.addedOn
                                  ? new Date(item.addedOn).toLocaleDateString()
                                  : "-",
                              }}
                              columns={[
                                "id",
                                "role",
                                "location",
                                "url",
                                "status",
                                "addedOn",
                              ]}
                              hideIcons={false}
                              onEdit={() =>
                                navigate(`/career/update/${item.id}`)
                              }
                              onDelete={() => handleDelete(item.id)}
                              pageLevelAccessData={pageAccessDetails}
                            />
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                )}

                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalEntries={filteredData.length}
                  entriesPerPage={entriesPerPage}
                  onPageChange={setCurrentPage}
                />
              </div>
            </div>
          </div>
        </div>
      ) : (
        ""
      )}
    </>
  );
};
